<template>
  <span>
    <v-btn
      color="primary"
      small
      depressed
      class="pa-0"
      :disabled="disabled"
      :loading="busyImporting"
      @click.stop="initiateModal()"
    >
      <v-icon small>$import</v-icon>
      <span class="pl-1">{{ $t('settings.similarityGroups.upload.uploadButton') }}</span>
    </v-btn>
    <v-dialog v-model="uploadDialog" width="500">
      <v-card>
        <v-card-title class="headline lighten-2" primary-title>
          {{ $t('settings.similarityGroups.upload.dialogHeading') }}
        </v-card-title>
        <v-icon class="close-button" color="grey" @click="closeModal()">close</v-icon>
        <file-upload
          v-if="isInitial"
          :upload-field-name="uploadFieldName"
          :is-saving="isSaving"
          :tool-store-group-independent="true"
          source="similarityGroups"
          @save="save"
        />
        <div v-if="isValidated">
          <v-card-text class="py-0">
            <v-container fluid>
              <div class="dialog-body">
                <p>
                  <b>{{ $t('settings.similarityGroups.upload.summary') }}</b>
                </p>
                <ul class="mb-3">
                  <li>
                    {{
                      $tc('settings.similarityGroups.upload.groupsCreated', summary.groupsCreated, {
                        count: summary.groupsCreated,
                      })
                    }}
                  </li>
                  <li>
                    {{
                      $tc(
                        'settings.similarityGroups.upload.productsAssigned',
                        summary.productsAssigned,
                        { count: summary.productsAssigned }
                      )
                    }}
                  </li>
                  <li>
                    {{
                      $tc(
                        'settings.similarityGroups.upload.productsReassigned',
                        summary.productsReassigned,
                        { count: summary.productsReassigned }
                      )
                    }}
                  </li>
                </ul>
                <p>{{ $t('settings.similarityGroups.upload.confirmUpload') }}</p>
              </div>
            </v-container>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" class="dialog-button" outlined depressed @click="closeModal()">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-tooltip :disabled="!hasNoChanges" top>
              <template #activator="{ on }"
                ><span class="ml-2" v-on="on">
                  <v-btn
                    class="primary dialog-button"
                    depressed
                    :disabled="isUploading || hasNoChanges"
                    :loading="busyImporting"
                    @click="confirm()"
                  >
                    {{ $t('actions.upload') }}
                  </v-btn>
                </span></template
              >
              <span>{{ $t('settings.similarityGroups.upload.nothingToUpload') }}</span>
            </v-tooltip>
          </v-card-actions>
        </div>
        <div v-if="isUploading">
          <v-card-text class="py-0">
            <v-container fluid>
              <div class="dialog-body">
                {{ $t('settings.similarityGroups.upload.uploadingState') }}
              </div>
            </v-container>
          </v-card-text>
        </div>
        <div v-if="isFailed && uploadError">
          <v-card-text>
            <v-container>
              <div v-if="hasTooManyRows">
                {{ $t('settings.similarityGroups.upload.tooManyRows', { maxRows }) }}
              </div>
              <upload-error-card
                v-if="hasUnknownProductKeyDisplays"
                :error-message="$t('settings.similarityGroups.upload.unknownProductKeyDisplays')"
                :details="uploadError.unknownProductKeyDisplays"
                unique-key-prefix="unknownProductKeyDisplays"
              />
              <upload-error-card
                v-if="hasMissingCannGroupProducts"
                :error-message="$t('settings.similarityGroups.upload.missingCannGroupProducts')"
                :details="uploadError.missingCannGroupProducts"
                unique-key-prefix="missingCannGroupProducts"
              />
              <upload-error-card
                v-if="hasEmptySimGroupDescriptions"
                :error-message="$t('settings.similarityGroups.upload.emptySimGroupDescriptions')"
                :details="uploadError.emptySimGroupDescriptions"
                unique-key-prefix="emptySimGroupDescriptions"
              />
              <upload-error-card
                v-if="hasCannGroupMismatchProducts"
                :error-message="$t('settings.similarityGroups.upload.cannGroupMismatchProducts')"
                :details="uploadError.cannGroupMismatchProducts"
                unique-key-prefix="cannGroupMismatchProducts"
              />
              <upload-error-card
                v-if="hasUomMismatchProducts"
                :error-message="$t('settings.similarityGroups.upload.uomMismatchProducts')"
                :details="uploadError.uomMismatchProducts"
                unique-key-prefix="uomMismatchProducts"
              />
              <upload-error-card
                v-if="hasMissingColumns"
                :error-message="$t('settings.similarityGroups.upload.missingColumns')"
                :details="uploadError.missingColumns"
                unique-key-prefix="missingColumns"
              />
            </v-container>
          </v-card-text>
          <v-divider />
          <v-card-actions class="btn-group">
            <v-spacer />
            <v-btn class="primary" depressed @click="closeModal()">{{ $t('actions.close') }}</v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import axios from 'axios';
import to from 'await-to-js';
import { mapState } from 'vuex';
import { isEmpty, get } from 'lodash';
import states from '@enums/upload-states';

export default {
  name: 'SimilarityGroupsUpload',

  props: {
    /** Disables the upload button when true (e.g. in weekly workpackages). */
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      uploadDialog: false,
      uploadError: null,
      currentStatus: states.initial,
      uploadFieldName: 'file',
      busyImporting: false,
      summary: {},
    };
  },

  computed: {
    ...mapState('workpackages', ['selectedWorkpackage']),
    ...mapState('clientConfig', ['uploadConfig']),

    isInitial() {
      return this.currentStatus === states.initial;
    },
    isSaving() {
      return this.currentStatus === states.saving;
    },
    isValidated() {
      return this.currentStatus === states.validated;
    },
    isFailed() {
      return this.currentStatus === states.failed;
    },
    isUploading() {
      return this.currentStatus === states.uploading;
    },
    hasUnknownProductKeyDisplays() {
      return !isEmpty(get(this.uploadError, 'unknownProductKeyDisplays', []));
    },
    hasMissingCannGroupProducts() {
      return !isEmpty(get(this.uploadError, 'missingCannGroupProducts', []));
    },
    hasEmptySimGroupDescriptions() {
      return !isEmpty(get(this.uploadError, 'emptySimGroupDescriptions', []));
    },
    hasCannGroupMismatchProducts() {
      return !isEmpty(get(this.uploadError, 'cannGroupMismatchProducts', []));
    },
    hasUomMismatchProducts() {
      return !isEmpty(get(this.uploadError, 'uomMismatchProducts', []));
    },
    hasMissingColumns() {
      return !isEmpty(get(this.uploadError, 'missingColumns', []));
    },
    hasTooManyRows() {
      return !!get(this.uploadError, 'tooManyRows', false);
    },
    maxRows() {
      return get(this.uploadConfig, 'similarityGroupsUpload.maxRows');
    },
    hasNoChanges() {
      return (
        !this.summary.groupsCreated &&
        !this.summary.productsAssigned &&
        !this.summary.productsReassigned
      );
    },
  },

  methods: {
    /** Open the dialog and reset any previous state. */
    initiateModal() {
      this.uploadDialog = true;
      this.reset();
    },

    /** Close the dialog and reset state. */
    closeModal() {
      this.uploadDialog = false;
      this.reset();
    },

    /**
     * Handle the file emitted by the file-upload child component.
     * POSTs to the validate-and-cache endpoint, stores the diff summary on success.
     * Transitions through saving → validated on success, or saving → failed on error.
     * @param {FormData} formData - Multipart form data from the file-upload component
     */
    async save(formData) {
      this.currentStatus = states.saving;
      try {
        const [err, res] = await to(
          axios.post(`/api/similarity-groups/${this.selectedWorkpackage._id}/import`, formData, {
            headers: {
              // Prevent axios from overriding Content-Type for FormData uploads.
              // See: https://github.com/bmuenzenmeyer/axios-1.0.0-migration-guide
              'Content-Type': undefined,
            },
          })
        );
        if (err) throw err.response.data;
        this.summary = res.data.summary;
        this.currentStatus = states.validated;
      } catch (error) {
        this.uploadError = error;
        this.currentStatus = states.failed;
      }
    },

    /**
     * Confirm the import by POSTing to the confirm endpoint.
     * Sets busyImporting for the duration of the request.
     * Closes the modal on success or transitions to failed on error.
     */
    async confirm() {
      this.currentStatus = states.uploading;
      this.busyImporting = true;
      try {
        const [err] = await to(
          axios.post(`/api/similarity-groups/${this.selectedWorkpackage._id}/import/confirm`)
        );
        if (err) throw new Error(err.message);
        this.closeModal();
      } catch {
        this.currentStatus = states.failed;
      } finally {
        this.busyImporting = false;
      }
    },

    reset() {
      this.currentStatus = states.initial;
      this.uploadError = null;
      this.summary = {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
.dialog-body {
  font-size: 1.4rem;
}
</style>
